import React from 'react';
import EfficiencyScore from './EfficiencyScore';
import { AlertCircle, TrendingUp, TrendingDown } from 'lucide-react';


const Dashboard = ({ data, opportunities }) => {
  if (!data) return <div>Loading...</div>; 
  const { metrics, efficiency_score, score_breakdown } = data;

  const MetricCard = ({ title, metric, description }) => {
    const isNegative = metric.gap < 0;
    const gapColor = isNegative ? 'text-red-600' : 'text-green-600';

    return (
      <div className="metric-card">
        <div className="metric-header">
          <h3>{title}</h3>
          {Math.abs(metric.gap_percentage) > 10 && (
            <AlertCircle className="w-5 h-5 text-amber-500" />
          )}
        </div>

        <div className="metric-body">
          <div className="metric-values">
            <div className="current-value">
              <span className="label">Current</span>
              <span className="value">
                {typeof metric.current === 'number' ? 
                  `£${metric.current.toFixed(2)}` : 
                  metric.current.toFixed(2)
                }
              </span>
            </div>
            
            <div className="target-value">
              <span className="label">Target</span>
              <span className="value">
                {typeof metric.target === 'number' ? 
                  `£${metric.target.toFixed(2)}` : 
                  metric.target.toFixed(2)
                }
              </span>
            </div>
          </div>

          <div className={`metric-gap ${gapColor}`}>
            <div className="gap-icon">
              {isNegative ? <TrendingDown /> : <TrendingUp />}
            </div>
            <div className="gap-text">
              <span className="gap-value">
                {Math.abs(metric.gap).toFixed(2)} gap
              </span>
              <span className="gap-percentage">
                ({Math.abs(metric.gap_percentage).toFixed(1)}% 
                {isNegative ? ' below' : ' above'} target)
              </span>
            </div>
          </div>

          {description && (
            <p className="metric-description">{description}</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-container">
      {/* Efficiency Score - Prominent Display */}
      <div className="score-section">
        <EfficiencyScore 
          score={efficiency_score}
          breakdown={score_breakdown}
        />
        
        <div className="score-interpretation">
          <h3>What This Means</h3>
          {efficiency_score >= 85 && (
            <p className="score-excellent">
              Outstanding efficiency! You're operating at industry-leading levels. 
              Focus on maintaining these standards and look for marginal gains.
            </p>
          )}
          {efficiency_score >= 70 && efficiency_score < 85 && (
            <p className="score-good">
              Good efficiency with room for improvement. You're above average but have 
              clear opportunities to capture additional value.
            </p>
          )}
          {efficiency_score >= 50 && efficiency_score < 70 && (
            <p className="score-fair">
              Moderate efficiency. Significant opportunities exist to improve performance 
              through better scheduling, pricing, and operations.
            </p>
          )}
          {efficiency_score < 50 && (
            <p className="score-poor">
              Substantial efficiency gaps. The good news: major improvement potential 
              exists within your control. Start with the highest-impact opportunities.
            </p>
          )}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="metrics-grid">
        <MetricCard
          title="Revenue Per Hour"
          metric={metrics.revenue_per_hour}
          description="How much revenue you generate per working hour. Higher is better."
        />

        <MetricCard
          title="Clients Per Hour"
          metric={metrics.clients_per_hour}
          description="Your scheduling efficiency. More clients = less wasted time."
        />

        <MetricCard
          title="Average Ticket"
          metric={metrics.avg_ticket}
          description="Average service value. Reflects pricing and upselling effectiveness."
        />
      </div>

      {/* Top Opportunities Teaser */}
      {opportunities && opportunities.opportunities.length > 0 && (
        <div className="opportunities-preview">
          <div className="preview-header">
            <h2>Your Top 3 Opportunities</h2>
            <p className="potential-value">
              Combined potential: £
              {opportunities.opportunities
                .slice(0, 3)
                .reduce((sum, opp) => sum + opp.annual_opportunity, 0)
                .toLocaleString()
              }/year
            </p>
          </div>

          <div className="opportunities-cards">
            {opportunities.opportunities.slice(0, 3).map((opp, index) => (
              <div key={opp.id} className="opportunity-preview-card">
                <div className="opp-number">{index + 1}</div>
                <div className="opp-content">
                  <h3>{opp.title}</h3>
                  <div className="opp-value">
                    £{opp.annual_opportunity.toLocaleString()}/year
                  </div>
                  <div className="opp-badges">
                    <span className={`badge impact-${opp.impact.toLowerCase()}`}>
                      {opp.impact} Impact
                    </span>
                    <span className={`badge effort-${opp.effort.toLowerCase()}`}>
                      {opp.effort} Effort
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button 
            className="view-all-button"
            onClick={() => window.scrollTo(0, 0)} // In real app, would switch tabs
          >
            View All Opportunities →
          </button>
        </div>
      )}

      {/* Reality Check Box */}
      <div className="reality-check-box">
        <h3>🎯 The Reality</h3>
        <p>
          You have <strong>£{opportunities?.summary?.total_annual_value?.toLocaleString() || 0}</strong> in 
          efficiency opportunities that you can capture <strong>without</strong>:
        </p>
        <ul>
          <li>❌ Waiting for a VAT cut</li>
          <li>❌ Government intervention</li>
          <li>❌ Lobbying Parliament</li>
          <li>❌ Blaming external factors</li>
        </ul>
        <p className="reality-emphasis">
          <strong>These opportunities exist within your control today.</strong>
        </p>
      </div>
    </div>
  );
};

export default Dashboard;